import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  ContentSection,
  Fieldset,
  Heading,
  Layout,
  PageWrap,
  SectionHeading,
} from '@vwfs-bronson/bronson-react';
import { Page } from '../../../..';
import { PaginationComponent } from '../../../../components/Pagination';
import { DealerDashboardSubpage, QuoteFlow } from '../../../../resources/base/models/enums';
import { routes } from '../../../../routing/routes';
import { getCreditApplicationFilteredList } from '../../../../services/api/miles/miles-credit-services';
import {
  cancelQuote,
  getQuote,
  getQuotes,
  updateQuoteSalesPerson,
} from '../../../../services/api/miles/miles-services';
import { formatDateWithPoints, formatDecimalComma, formatPrice } from '../../../../services/common/format';
import { assertDefined, getDisplayedManufacturerLabel, isNotesEnabled } from '../../../../services/common/miscUtils';
import { staticSelector, store } from '../../../../services/redux';
import { brokerSelectors } from '../../../../services/redux/features/broker.slice';
import { setIsCarUsed, setSelectedVehicleType } from '../../../../services/redux/features/custom.slice';
import { setCustomerInitialState } from '../../../../services/redux/features/customer.slice';
import {
  clearSelectedComparisonOfferIds,
  dashboardSelectors,
  resetDashboardSearchForm,
  setDashboardSearchFormWithSelections,
  setSubPageLabelStatusIds,
} from '../../../../services/redux/features/dashboard.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { setQuoteData, setQuoteFlow } from '../../../../services/redux/features/quote.slice';
import { getInitialCustomerStateFromQuote } from '../../../../services/redux/reduxUtils';
import { useNavigate } from '../../../../services/routing/navigate';
import { calculateQuoteFinancedAmount } from '../../../FormPage/Step3/calculations';
import { getVehicleType, handleCopyQuote, isCarUsedFromQuote } from '../../../utils';
import { ButtonAction, Buttons } from '../../constants/ButtonAction';
import { paginationInitialState } from '../../constants/DefaultPagination';
import {
  defaultSelectedStudiesSubpage,
  isButtonDisabledByStatus,
  offerStatusIdOptionsList,
  OfferStatusIdType,
} from '../../constants/StatusMap';
import { DashboardActionPopup, DashboardPopupTransfer } from '../../popups/DashboardPopups';
import { DashBoardSearchSection } from '../components/DashBoardSearchSection';
import { DashboardSubpagesTabs } from '../components/DashboardSubpagesTabs';
import { DashBoardTable } from '../components/DashBoardTable';
import { useDashboardNotesUtils } from '../utils/dashboardNotesUtils';
import { handleSelectionAfterTableUpdate } from '../utils/dashboardSubpageTableSelection';
import { initializeSalesPersonDropdown } from '../utils/initializeSalesPersonDropdown';
import { mapResponsePagination } from '../utils/mapResponsePagination';
import { checkIfButtonShouldBeDisabled } from '../utils/multiselectionButtonCheck';
import { SearchedValues } from '../utils/searchedValues.types';
import { setFinancialProductList } from '../utils/setFinancialProductDropdown';
import { MappedResponseQuote } from './models';

const tableHeaders = [
  'offerReference',
  ...(isNotesEnabled() ? ['notes'] : []),
  'tradingName',
  'siren',
  'usage',
  'manufacturer',
  'model',
  'financialProduct',
  'eligibility',
  'grossLoanAmount',
  'durationMileage',
  'spreadLeasePrice',
  'statusId',
  'offreplus',
  'validityDate',
];

const buttonList = [
  { value: 'Copier', label: 'Copier', enableOnMultiSelect: false },
  { value: 'Annuler', label: 'Annuler', enableOnMultiSelect: false, disabledOnStatuses: ['100051', '100045', '22'] },
  { value: 'Transfert', label: 'Transfert', enableOnMultiSelect: true, disabledOnStatuses: ['100051', '100045', '22'] },
  { value: 'Documents', label: 'Documents', enableOnMultiSelect: false },
] as Buttons;

export const DashboardSubpageStudies: FunctionComponent = () => {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showCopyPopup, setShowCopyPopup] = useState(false);
  const [showModifyPopup, setShowModifyPopup] = useState(false);
  const [modifyPopupText, setModifyPopupText] = useState<string>('');
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [showTransfertPopup, setShowTransfertPopup] = useState(false);
  const [tableData, setTableData] = useState<MappedResponseQuote[]>([]);
  const [chosenSalesPerson, setChosenSalesPerson] = useState('');
  const [pagination, setPagination] = useState(paginationInitialState);
  const [contractsTotalCount, setContractsTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getNotesDashboardButtonFromConversations } = useDashboardNotesUtils();

  const [searchedValues, setSearchedValues] = useState<SearchedValues>({});
  const [salesPersons, setSalesPersons] = useState<Array<{ value: string; label: string }>>([]);
  const [financialProductDropdownList, setFinancialProductDropdownList] = useState<
    ReadonlyArray<{ value: string; label: string }>
  >([]);

  const milesSalesPersonId = useSelector(brokerSelectors.milesSalesPersonId);
  const searchForm = useSelector(dashboardSelectors.selectDashboardSearchFormWithSelections);
  const hasStoredSearch = !!searchForm && Object.keys(searchForm).length > 0;

  const tableRef = useRef<{ clearSelections: () => void } | null>(null);

  const subPageLabelStatusIds: OfferStatusIdType[] | undefined = useSelector(
    dashboardSelectors.selectSubPageLabelStatusIds
  );

  const overallFilterStatusIds: OfferStatusIdType[] = subPageLabelStatusIds || defaultSelectedStudiesSubpage;

  const resetTableSelections = () => {
    if (tableRef.current) {
      tableRef.current.clearSelections(); // Call the reset method on the child
    }
  };

  const mapResponseQuote = (
    quote: QuoteSchema.Schemas.QuoteResponse,
    creditApplicationList: CreditIntegration.Schemas.GetListCreditApplicationsResponse
  ): MappedResponseQuote => {
    const customerType = quote.customerData?.customerType;
    const isPrivate = customerType === 'INDIVIDUAL';
    const statusId = quote?.financialProduct?.offers?.[0].statusId as OfferStatusIdType | undefined;
    const description = quote.vehicleData?.model?.description ?? '';
    const financedAmount = calculateQuoteFinancedAmount(quote);
    const manufacturer = quote?.vehicleData?.model?.manufacturer;

    const creditApplication = creditApplicationList?.creditApplications?.find(
      (creditApplication) =>
        creditApplication?.financialProduct?.offers?.[0]?.offerId === quote?.financialProduct?.offers?.[0]?.offerId
    );

    const notes = creditApplication
      ? getNotesDashboardButtonFromConversations(
          'CREDIT_APPLICATION',
          quote?.financialProduct?.offers?.[0]?.offerReference,
          creditApplication.conversations
        )
      : null;

    return {
      offerId: quote?.financialProduct?.offers?.[0]?.offerId,
      offerReference: quote?.financialProduct?.offers?.[0]?.offerReference,
      tradingName: quote?.customerData?.tradingName || quote?.customerData?.companyData?.name,
      siren: quote?.customerData?.companyData?.companyRegistrationNumber,
      usage: isPrivate
        ? t('dashboard:table:body:privateCustomerUsage')
        : t('dashboard:table:body:businessCustomerUsage'),
      manufacturer: manufacturer ? getDisplayedManufacturerLabel(manufacturer) : undefined,
      model: description.substring(description.indexOf(' ') + 1),
      financialProduct: quote?.financialProduct?.offers?.[0].productDescription,
      eligibility: formatDecimalComma(quote?.financialProduct?.offers?.[0].calculation?.eligibility),
      grossLoanAmount: formatPrice(financedAmount),
      durationMileage:
        (quote.financialProduct?.offers?.[0].calculation?.duration ?? '') +
        '/' +
        (quote.financialProduct?.offers?.[0].calculation?.contractMileage ?? ''),
      spreadLeasePrice: formatPrice(
        quote?.financialProduct?.offers?.[0].calculation?.spreadLeasePrice?.grossAmount ?? 0
      ),
      statusId: statusId ? offerStatusIdOptionsList[statusId]?.label : undefined,
      rawStatusId: statusId,
      offreplus: quote?.financialProduct?.offers?.[0]?.offerPlus,
      validityDate: formatDateWithPoints(quote?.financialProduct?.offers?.[0].validityEndDate),
      notes,
    };
  };

  const mapQuotes = async (quotes: QuoteSchema.Schemas.QuoteResponse[]): Promise<MappedResponseQuote[]> => {
    if (!quotes?.length) {
      return [];
    }

    const offerIdList = quotes?.map((quote) => quote?.financialProduct?.offers?.[0]?.offerId);

    const creditApplications = await getCreditApplicationFilteredList({
      offerIds: offerIdList?.join(','),
      includeConversationInfo: 'true',
    });

    return quotes?.map((quote) => mapResponseQuote(quote, creditApplications));
  };

  const loadQuotes = async (data: Partial<Page> = {}) => {
    const currentCompany = staticSelector(brokerSelectors.currentCompany);
    const milesCompanyIds = [assertDefined(currentCompany?.milesCompanyId)];

    try {
      store.dispatch(loadingActions.setLoading(true));
      const requestData = data?.statusId
        ? data
        : {
            ...data,
            statusId: overallFilterStatusIds,
          };
      const response = await getQuotes({
        milesCompanyIds,
        ...(searchForm.currentPage ? { page: searchForm.currentPage } : {}),
        ...requestData,
      });
      setContractsTotalCount(response?.totalCount ?? 0);
      const tableData = await mapQuotes(response?.quotes ?? []);
      setTableData(tableData);
      setSelectedItems((prev) => handleSelectionAfterTableUpdate(prev, tableData, resetTableSelections));
      setPagination(mapResponsePagination(response));
      store.dispatch(loadingActions.setLoading(false));
    } catch {
      store.dispatch(loadingActions.setLoading(false));
    }
  };

  const cancelRow = async () => {
    try {
      store.dispatch(loadingActions.setLoading(true));
      await cancelQuote(selectedItems[0]);
      store.dispatch(loadingActions.setLoading(false));
    } catch {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
      return;
    }

    // !!!
    // TODO temporary solution due to very long request processing through services
    // Will be discussed with the team to find the best solution

    setTimeout(async () => {
      try {
        store.dispatch(loadingActions.setLoading(true));
        await loadQuotes();
        store.dispatch(loadingActions.setLoading(false));
      } catch {
        store.dispatch(loadingActions.setLoading(false));
      }
    }, 3000);
  };

  const handleButtonClick = (action: ButtonAction) => {
    switch (action) {
      case 'Copier':
        setShowCopyPopup(true);
        break;
      case 'Annuler':
        setShowCancelPopup(true);
        break;
      case 'Transfert':
        setShowTransfertPopup(true);
        break;
      case 'Documents':
        dispatch(
          setDashboardSearchFormWithSelections({
            ...searchedValues,
            selectedCheckboxes: selectedItems,
            currentPage: pagination?.currentPage,
          })
        );
        navigate.url(`${routes.dashboardStudiesDocuments.replace(':id', selectedItems[0])}`);
        break;
      default:
        break;
    }
  };

  const modifyRow = async () => {
    try {
      store.dispatch(loadingActions.setLoading(true));
      const quoteData = await getQuote(selectedItems[0]);
      store.dispatch(setIsCarUsed(isCarUsedFromQuote(quoteData)));
      const customerInitialState = await getInitialCustomerStateFromQuote(quoteData);
      store.dispatch(setCustomerInitialState(customerInitialState));
      store.dispatch(setSelectedVehicleType(getVehicleType(quoteData)));
      store.dispatch(setQuoteData(quoteData));
      store.dispatch(setQuoteFlow(QuoteFlow.MODIFY));
      store.dispatch(loadingActions.setLoading(false));
      navigate.form(`/3`);
    } catch {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
    }
  };

  const copySelectedQuote = async () => {
    await handleCopyQuote(selectedItems[0], navigate, 3);
  };

  const handleConfirm = async (action: ButtonAction) => {
    switch (action) {
      case 'Copier':
        setShowCopyPopup(false);
        copySelectedQuote();
        break;
      case 'Modifier':
        setShowModifyPopup(false);
        modifyRow();
        break;
      case 'Annuler':
        cancelRow();
        setShowCancelPopup(false);
        break;
      case 'Transfert':
        try {
          store.dispatch(loadingActions.setLoading(true));
          await Promise.all(selectedItems.map((item) => updateQuoteSalesPerson(chosenSalesPerson, item)));
        } catch (err) {
          navigate.error();
        } finally {
          resetTableSelections();
          setSelectedItems([]);
          setShowTransfertPopup(false);
          store.dispatch(loadingActions.setLoading(false));
        }
        break;
      default:
        break;
    }
  };

  const onTableSelection = (selectedIds: string[]) => {
    setSelectedItems(selectedIds);
  };

  const paginationOnChange = (page: number) => {
    loadQuotes({ page, ...searchedValues });
    resetTableSelections();
    setSelectedItems([]);
  };

  const searchFieldsMap = (values: Partial<SearchedValues>): SearchedValues => ({
    ...(values?.searchQuery && { searchQuery: values?.searchQuery }),
    ...(values?.salesPerson && { salesPerson: values?.salesPerson }),
    ...(values?.statusId && { statusId: values?.statusId }),
    ...(values?.financialProduct && { financialProduct: values?.financialProduct }),
    pageSize: pagination?.pageSize,
  });

  const searchQuotes = (values: Partial<SearchedValues>) => {
    dispatch(setSubPageLabelStatusIds(undefined));
    const newSearchedValues: SearchedValues = searchFieldsMap(values);
    setSearchedValues(newSearchedValues);
    loadQuotes({
      ...newSearchedValues,
      statusId: newSearchedValues.statusId ?? defaultSelectedStudiesSubpage,
    });
  };

  useEffect(() => {
    dispatch(clearSelectedComparisonOfferIds());
    const fetchData = async () => {
      if (hasStoredSearch) {
        dispatch(resetDashboardSearchForm());
        searchQuotes(searchForm);
      } else if (milesSalesPersonId) {
        const defaultFilter = { salesPerson: milesSalesPersonId };
        setSearchedValues(defaultFilter);
        await loadQuotes(defaultFilter);
      } else {
        await loadQuotes();
      }

      await initializeSalesPersonDropdown(setSalesPersons);
      setFinancialProductDropdownList((await setFinancialProductList()) || []);
    };

    fetchData();
    return () => {
      dispatch(setSubPageLabelStatusIds(undefined));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRowDbClick = (selectedId: string) => {
    setSelectedItems([selectedId]);
    const selectedRow = tableData.find((row) => row.offerId === selectedId);

    if (selectedRow?.rawStatusId === '100051') {
      setModifyPopupText(t('dashboard:popup:inactiveOfferPopup'));
    } else if (selectedRow?.rawStatusId === '100045' || selectedRow?.rawStatusId === '22') {
      setModifyPopupText(t('dashboard:popup:modifyCancelledPopup'));
    } else {
      setModifyPopupText(t('dashboard:popup:modifyReadOnlyQuotePopup'));
    }

    setShowModifyPopup(true);
  };

  const selectedStatusIds = tableData
    .filter((row) => selectedItems.includes(row.offerId ?? ''))
    .map((row) => row.rawStatusId);

  return (
    <>
      <ContentSection pageWrap className="u-pt-none">
        <DashboardSubpagesTabs />
      </ContentSection>

      <ContentSection className="u-pv-small" pageWrap>
        <Layout>
          <Layout.Item default="1/1" s="1/1" className="u-mt-none">
            <Heading level={2}>{t('dashboard:search:header')}</Heading>
          </Layout.Item>
        </Layout>
      </ContentSection>

      <PageWrap>
        <Layout>
          <Layout.Item default="1/1" s="1/1" className="u-mt-none">
            <DashBoardSearchSection
              searchQuotes={searchQuotes}
              subpage={DealerDashboardSubpage.Studies}
              fields={['salesPerson', 'statusId', 'financialProduct']}
              salesPersonDropdown={salesPersons}
              searchFieldFinancialProductDropdownValues={financialProductDropdownList}
              searchQuery
              defaultSalesPerson={milesSalesPersonId}
            />
          </Layout.Item>
        </Layout>
      </PageWrap>

      <ContentSection pageWrap>
        <Layout>
          <Layout.Item className="u-text-center u-mt-none">
            <SectionHeading level={3} className="u-text-left">
              {t('dashboard:search:results', {
                numResults: contractsTotalCount,
              })}
            </SectionHeading>
            <DashBoardTable
              ref={tableRef}
              headers={tableHeaders}
              onSelection={onTableSelection}
              values={tableData}
              onDoubleClick={onRowDbClick}
            />
            <Layout center>
              <PaginationComponent pagination={pagination} onChange={paginationOnChange} />
            </Layout>
          </Layout.Item>
        </Layout>
      </ContentSection>

      <Fieldset.Row>
        <ButtonContainer center>
          <Layout.Item className="u-text-center">
            {buttonList?.map(({ label, value, enableOnMultiSelect, disabledOnStatuses }) => (
              <Button
                key={`${label}`}
                value={`${value}`}
                type="button"
                secondary
                className="u-m-small"
                onClick={() => handleButtonClick(label)}
                disabled={
                  checkIfButtonShouldBeDisabled(selectedItems, enableOnMultiSelect) ||
                  isButtonDisabledByStatus(disabledOnStatuses, selectedStatusIds)
                }
              >
                {label}
              </Button>
            ))}
            {showModifyPopup && (
              <DashboardActionPopup
                handleClose={() => setShowModifyPopup(false)}
                onConfirm={() => handleConfirm('Modifier')}
                text={modifyPopupText}
              />
            )}
            {showCopyPopup && (
              <DashboardActionPopup
                handleClose={() => setShowCopyPopup(false)}
                onConfirm={() => handleConfirm('Copier')}
                text={t('dashboard:popup:copyTextTitle')}
              />
            )}

            {showCancelPopup && (
              <DashboardActionPopup
                handleClose={() => setShowCancelPopup(false)}
                onConfirm={() => handleConfirm('Annuler')}
                text={t('dashboard:popup:cancelTextTitle')}
              />
            )}

            {showTransfertPopup && (
              <DashboardPopupTransfer
                handleClose={() => setShowTransfertPopup(false)}
                onConfirm={() => handleConfirm('Transfert')}
                salesPersons={salesPersons}
                chooseSalesPerson={setChosenSalesPerson}
                quoteIds={selectedItems}
                subpage={DealerDashboardSubpage.Studies}
              />
            )}
          </Layout.Item>
        </ButtonContainer>
      </Fieldset.Row>
    </>
  );
};
